import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import { cn } from '@/lib/utils'

/**
 * TooltipProvider must wrap the application root (App.tsx) once.
 * Re-exported here so callers don't need to import from @radix-ui directly.
 */
export const TooltipProvider = TooltipPrimitive.Provider

interface TooltipProps {
  /** The tooltip content */
  content: React.ReactNode
  /** The trigger element — must accept a ref */
  children: React.ReactNode
  side?: 'top' | 'right' | 'bottom' | 'left'
  align?: 'start' | 'center' | 'end'
  /** Milliseconds before the tooltip opens (default 400) */
  delayDuration?: number
  className?: string
  /** Prevent tooltip from rendering (useful for conditional tooltips) */
  disabled?: boolean
}

/**
 * Accessible tooltip built on Radix UI Tooltip.
 *
 * Requires `<TooltipProvider>` as an ancestor (see App.tsx).
 *
 * Usage:
 * ```tsx
 * <Tooltip content="View birth chart details">
 *   <Button variant="ghost" size="sm"><Eye className="w-4 h-4" /></Button>
 * </Tooltip>
 * ```
 */
export function Tooltip({
  content,
  children,
  side = 'top',
  align = 'center',
  delayDuration = 400,
  className,
  disabled = false,
}: TooltipProps) {
  if (disabled) return <>{children}</>

  return (
    <TooltipPrimitive.Root delayDuration={delayDuration}>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          align={align}
          sideOffset={6}
          className={cn(
            'z-50 px-2.5 py-1.5 rounded-lg',
            'bg-cosmic-aurora text-star-white text-xs font-medium leading-snug',
            'ring-1 ring-white/10',
            'max-w-[200px]',
            'animate-slide-up',
            'select-none pointer-events-none',
            className,
          )}
        >
          {content}
          <TooltipPrimitive.Arrow
            className="fill-cosmic-aurora"
            width={10}
            height={5}
          />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  )
}
